/**
 * ThemeService - Manages app theming and dark mode
 * 
 * Handles theme switching, color application, and system preference detection.
 */

import { storageService } from './StorageService';
import { configService } from './ConfigService';

class ThemeService {
  constructor() {
    this.currentTheme = 'light';
    this.systemPrefersDark = false;
    this.mediaQuery = null;
  }

  /**
   * Initialize the theme service
   */
  initialize() {
    // Detect system preference
    this.detectSystemPreference();

    // Load saved preference or use system default
    const savedTheme = storageService.getSetting('theme', 'system');
    this.applyTheme(savedTheme);

    // Listen for system preference changes
    this.listenForSystemChanges();

    return this.currentTheme;
  }

  /**
   * Detect system color scheme preference
   */
  detectSystemPreference() {
    if (window.matchMedia) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.systemPrefersDark = this.mediaQuery.matches;
    }
  }

  /**
   * Listen for system theme changes
   */
  listenForSystemChanges() {
    if (this.mediaQuery) {
      this.mediaQuery.addEventListener('change', (e) => {
        this.systemPrefersDark = e.matches;
        
        // Only auto-switch if user preference is 'system'
        const savedTheme = storageService.getSetting('theme', 'system');
        if (savedTheme === 'system') {
          this.applyTheme('system');
        }
      });
    }
  }

  /**
   * Apply a theme
   * @param {string} theme - 'light', 'dark', or 'system'
   */
  applyTheme(theme) {
    let effectiveTheme;
    
    if (theme === 'system') {
      effectiveTheme = this.systemPrefersDark ? 'dark' : 'light';
    } else {
      effectiveTheme = theme;
    }

    this.currentTheme = effectiveTheme;

    // Apply to document
    document.documentElement.setAttribute('data-theme', effectiveTheme);
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${effectiveTheme}`);

    // Apply Ionic theme
    document.body.classList.toggle('dark', effectiveTheme === 'dark');

    // Update meta theme-color
    this.updateMetaThemeColor(effectiveTheme);

    // Apply colors from config
    this.applyConfigColors(effectiveTheme);

    // Save preference
    storageService.saveSetting('theme', theme);

    return effectiveTheme;
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme() {
    const newTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
    return this.applyTheme(newTheme);
  }

  /**
   * Get current theme
   */
  getCurrentTheme() {
    return this.currentTheme;
  }

  /**
   * Get saved theme preference
   */
  getSavedPreference() {
    return storageService.getSetting('theme', 'system');
  }

  /**
   * Check if dark mode is active
   */
  isDarkMode() {
    return this.currentTheme === 'dark';
  }

  /**
   * Update the meta theme-color
   */
  updateMetaThemeColor(theme) {
    const config = configService.getConfig();
    if (!config) return;

    const colors = config.branding?.colors;
    if (!colors) return;

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const color = theme === 'dark' ? colors.backgroundDark : colors.primary;
      metaThemeColor.setAttribute('content', color);
    }
  }

  /**
   * Apply colors from config to CSS variables
   */
  applyConfigColors(theme) {
    const config = configService.getConfig();
    if (!config?.branding?.colors) return;

    const colors = config.branding.colors;
    const root = document.documentElement;

    // Apply all color variables
    Object.keys(colors).forEach(key => {
      root.style.setProperty(`--color-${this.camelToKebab(key)}`, colors[key]);
    });

    // Apply theme-specific colors
    if (theme === 'dark') {
      root.style.setProperty('--ion-background-color', colors.backgroundDark);
      root.style.setProperty('--ion-text-color', colors.textDark);
      root.style.setProperty('--ion-card-background', colors.surfaceDark);
      root.style.setProperty('--background', colors.backgroundDark);
      root.style.setProperty('--text-color', colors.textDark);
      root.style.setProperty('--text-secondary', colors.textSecondaryDark || '#aaaaaa');
    } else {
      root.style.setProperty('--ion-background-color', colors.background);
      root.style.setProperty('--ion-text-color', colors.text);
      root.style.setProperty('--ion-card-background', colors.surface);
      root.style.setProperty('--background', colors.background);
      root.style.setProperty('--text-color', colors.text);
      root.style.setProperty('--text-secondary', colors.textSecondary || '#666666');
    }

    // Primary color palette for Ionic
    root.style.setProperty('--ion-color-primary', colors.primary);
    root.style.setProperty('--ion-color-primary-shade', colors.primaryDark);
    root.style.setProperty('--ion-color-primary-tint', colors.primaryLight);

    // Secondary color
    root.style.setProperty('--ion-color-secondary', colors.secondary);
    root.style.setProperty('--ion-color-secondary-shade', colors.secondaryDark);
    root.style.setProperty('--ion-color-secondary-tint', colors.secondaryLight);

    // Status colors
    root.style.setProperty('--ion-color-success', colors.success);
    root.style.setProperty('--ion-color-warning', colors.warning);
    root.style.setProperty('--ion-color-danger', colors.error);
  }

  /**
   * Helper: Convert camelCase to kebab-case
   */
  camelToKebab(str) {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  }

  /**
   * Get CSS variable value
   */
  getCSSVariable(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  /**
   * Get color for use in components
   */
  getColor(colorName) {
    const config = configService.getConfig();
    const colors = config?.branding?.colors || {};
    
    // Check for theme-specific variants
    if (this.currentTheme === 'dark') {
      if (colorName === 'background') return colors.backgroundDark || '#1a1a1a';
      if (colorName === 'surface') return colors.surfaceDark || '#2a2a2a';
      if (colorName === 'text') return colors.textDark || '#e0e0e0';
      if (colorName === 'textSecondary') return colors.textSecondaryDark || '#aaaaaa';
    }

    return colors[colorName] || this.getCSSVariable(`--color-${this.camelToKebab(colorName)}`);
  }

  /**
   * Get code editor theme based on current theme
   */
  getCodeEditorTheme() {
    const config = configService.getConfig();
    const editorConfig = config?.ui?.codeEditor || {};
    
    if (this.currentTheme === 'dark') {
      return editorConfig.themeDark || 'dracula';
    }
    return editorConfig.theme || 'monokai';
  }
}

// Export singleton instance
export const themeService = new ThemeService();
export default themeService;